import { Fragment, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, ArrowLeft, Download, ExternalLink, FileText, ImagePlus, Loader2, Pencil, Plus, Tag, Trash2, Upload, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation } from '@/contexts/LocationContext';
import ProductImageWithHover from '@/features/purchase-orders/components/ProductImageWithHover';
import EditSkuModal, { type SkuCatalogItem } from '@/features/inventory/components/EditSkuModal';
import BatchEditVariantsModal, { type BatchVariant } from '@/features/inventory/components/BatchEditVariantsModal';
import { useVariantSelection } from '@/features/inventory/hooks/useVariantSelection';
import { SkuStatusBadge } from '@/features/inventory/components/SkuStatusBadge';
import { VariantCostCell } from '@/features/inventory/components/SkuCostDisplay';
import { SupplierOutChip } from '@/features/inventory/components/SupplierOutChip';
import { SupplyBadge } from '@/features/supplier-catalog/components/SupplyBadge';
import ArchiveSkusDialog from '@/features/inventory/components/ArchiveSkusDialog';
import {
  resolveProductStatus,
  toArchiveTarget,
  type ArchiveSkuTarget,
} from '@/features/inventory/utils/sku-status';
import { getShopifyAdminProductUrl } from '@/lib/shopify-urls';
import { toast } from 'sonner';
import { sortVariants } from '@/lib/variant-sort';
import { inventoryApi } from '@/features/inventory/api/inventoryApi';
import { BomCard } from '@/features/sku-components/components/BomCard';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { BackToTopPill } from '@/components/BackToTopPill';
import { PageLoader } from '@/components/loading/PageLoader';

function formatCost(value: number | null): string {
  if (value == null) return '-';
  return `$${value.toFixed(2)}`;
}

const WEIGHT_UNIT_ABBR: Record<string, string> = {
  GRAMS: 'g',
  KILOGRAMS: 'kg',
  OUNCES: 'oz',
  POUNDS: 'lb',
};

function formatWeight(value: number | null, unit: string | null): string {
  if (value == null) return '-';
  const abbr = unit ? WEIGHT_UNIT_ABBR[unit] ?? unit : '';
  return `${value} ${abbr}`.trim();
}

export default function ProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { can } = useRole();
  const { formatConverted } = useCurrencyFormatter();
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const utils = trpc.useUtils();
  const canManageSkus = can('skus.manage');
  // Cost columns and the per-location cost breakdown need skus.costs_manage; the server refuses cost writes without it.
  const canSeeCosts = can('skus.costs_manage');
  const { t } = useTranslation('inventory');

  // Expandable variant rows — shows per-location WAC and stock
  const [expandedVariantIds, setExpandedVariantIds] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((variantId: string) => {
    setExpandedVariantIds(prev => {
      const next = new Set(prev);
      if (next.has(variantId)) next.delete(variantId);
      else next.add(variantId);
      return next;
    });
  }, []);

  const { data: product, isLoading, error } = trpc.inventory.getProductDetail.useQuery(
    { productId: productId! },
    { enabled: !!productId, staleTime: 60_000 }
  );

  // Sort variants once for stable reference
  const sortedVariants = useMemo(
    () => (product ? sortVariants(product.variants) : []),
    [product],
  );

  // Selection state (admin only)
  const {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    isAllSelected,
    isSomeSelected,
    selectedCount,
  } = useVariantSelection({ items: sortedVariants });

  // Batch edit modal
  const [batchEditOpen, setBatchEditOpen] = useState(false);

  // Archive the selected variants (Admin only, own org: retireSkus acts on
  // the caller's org alone). This page lists live variants only, so there is
  // no Unarchive here; that lives behind Show archived on /products. Each
  // target carries the product's fresh Shopify status, the same value the
  // header badge uses.
  const [archiveTargets, setArchiveTargets] = useState<ArchiveSkuTarget[] | null>(null);
  const openArchive = () =>
    setArchiveTargets(
      sortedVariants.filter((v) => selectedIds.has(v.id)).map((v) => toArchiveTarget(v, product)),
    );
  // SKUs the server refused (they came into use since the check) stay
  // selected; everything else is cleared.
  const finishArchive = (refusedIds: string[]) => {
    const refused = new Set(refusedIds);
    for (const id of selectedIds) {
      if (!refused.has(id)) toggleSelect(id);
    }
  };

  const selectedVariantsForBatch = useMemo<BatchVariant[]>(
    () =>
      sortedVariants
        .filter((v) => selectedIds.has(v.id))
        .map((v) => ({ id: v.id, sku: v.sku, isLocked: v.isLocked })),
    [sortedVariants, selectedIds],
  );

  // Edit variant modal
  const [editingSku, setEditingSku] = useState<SkuCatalogItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Edit product dialog
  const [editProductOpen, setEditProductOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editProductType, setEditProductType] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editSpecPdfUrl, setEditSpecPdfUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);

  // Add variant dialog
  const [addVariantOpen, setAddVariantOpen] = useState(false);
  const [newVariantSku, setNewVariantSku] = useState('');
  const [newVariantSize, setNewVariantSize] = useState('');
  const [newVariantColor, setNewVariantColor] = useState('');

  const updateProductMutation = trpc.inventory.updateProductDetails.useMutation({
    onSuccess: async () => {
      toast.success('Product updated');
      setEditProductOpen(false);
      await invalidate();
    },
    onError: (err) => toast.error(err.message || 'Failed to update product'),
  });

  const openEditProduct = () => {
    if (!product) return;
    setEditTitle(product.title || '');
    setEditProductType(product.productType || '');
    setEditImageUrl(product.imageUrl || '');
    setEditSpecPdfUrl(product.specPdfUrl || '');
    setEditProductOpen(true);
  };

  const addVariantMutation = trpc.inventory.addVariant.useMutation({
    onSuccess: async () => {
      toast.success('Variant added');
      setAddVariantOpen(false);
      setNewVariantSku('');
      setNewVariantSize('');
      setNewVariantColor('');
      await invalidate();
    },
    onError: (err) => toast.error(err.message || 'Failed to add variant'),
  });

  const removeVariantMutation = trpc.inventory.removeVariant.useMutation({
    onSuccess: async () => {
      toast.success('Variant removed from product');
      await invalidate();
    },
    onError: (err) => toast.error(err.message || 'Failed to remove variant'),
  });

  // Delete product
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const deleteProductMutation = trpc.inventory.deleteProductGlobal.useMutation({
    onSuccess: async () => {
      toast.success('Product deleted');
      await utils.inventory.listProductsGrouped.invalidate();
      await utils.inventory.listSkus.invalidate();
      navigate('/products');
    },
    onError: (err) => toast.error(err.message || 'Failed to delete product'),
  });

  const invalidate = async () => {
    await utils.inventory.getProductDetail.invalidate({ productId: productId! });
    await utils.inventory.listProductsGrouped.invalidate();
    await utils.inventory.listSkus.invalidate();
    if (selectedLocation) {
      await utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
    }
  };

  const openEditVariant = (variant: NonNullable<typeof product>['variants'][number]) => {
    setEditingSku({
      id: variant.id,
      sku: variant.sku,
      title: variant.title,
      category: {
        type: variant.category ?? product?.productType ?? null,
        color: variant.color ?? null,
        size: variant.size ?? null,
      },
      imageUrl: variant.imageUrl,
      productionType: (product?.productionType ?? 'POD') as 'POD' | 'PAP',
      isTrim: variant.isTrim ?? false,
      isLocked: variant.isLocked,
      skuCostOverride: variant.skuCostOverride,
      skuCostOverrideCurrency: variant.skuCostOverrideCurrency,
      skuCostOverrideOrg: variant.skuCostOverrideOrg,
      hasWac: (variant.locations ?? []).some(l => l.weightedAverageCost != null),
      hsCode: variant.hsCode ?? null,
      weightValue: variant.weightValue ?? null,
      weightUnit: variant.weightUnit ?? null,
      supplierId: variant.supplierId ?? null,
      shopifyInventoryItemId: (variant as Record<string, unknown>).shopifyInventoryItemId as string | null ?? null,
      erpManagedFields: (variant as Record<string, unknown>).erpManagedFields as Record<string, { managedAt: string; managedBy: string }> | undefined,
    });
    setEditOpen(true);
  };

  // Trim products are POD blanks with no print step — surface that instead of
  // the raw production_type, which would read "POD" and mislead.
  const typeLabel = useMemo(() => {
    if (!product) return null;
    return product.variants.length > 0 && product.variants.every((v) => v.isTrim)
      ? 'Trim'
      : product.productionType;
  }, [product]);

  // The TopHeader slot carries a compact breadcrumb-style name only — the real
  // heading (title + badges) lives in the page hero below.
  usePageTitle(
    product ? product.title || 'Untitled Product' : null,
    null,
    { label: 'Back to Products', to: '/products' },
  );

  if (isLoading) {
    return <PageLoader pool="products" />;
  }

  if (error) {
    return (
      <div className="w-full pt-4 pb-8 px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Button>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 max-w-md">
          <h2 className="text-xl font-semibold text-destructive mb-2">Error Loading Product</h2>
          <p className="text-destructive">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="w-full pt-4 pb-8 px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Button>
        <div className="rounded-md border bg-muted/30 p-4 max-w-md">
          <h2 className="text-xl font-semibold text-muted-foreground mb-2">Product Not Found</h2>
          <p className="text-muted-foreground">The product could not be loaded.</p>
        </div>
      </div>
    );
  }

  const isPod = product.productionType === 'POD';
  const shopifyUrl = getShopifyAdminProductUrl(organization?.shopify_domain, product.shopifyProductId);
  const allVariantsArchived = product.variants.length === 0 && product.archivedVariantCount > 0;

  return (
    <div className="w-full pt-4 pb-8 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* "Back to Products" lives in the TopHeader (typewriter button); the
          heading itself lives here so the page has a real H1. Product-level
          actions sit in this header rather than a PageActionBar — Edit/Delete
          are the only actions, and an otherwise-empty sticky bar read as dead
          space above the fold (owner feedback 2026-07-14). */}
      <div className="flex items-start gap-5">
        <div className="shrink-0">
          <ProductImageWithHover
            imageUrl={product.imageUrl ?? undefined}
            productSku={product.title ?? ''}
            size="large"
          />
        </div>

        <div className="flex flex-1 min-w-0 items-start justify-between gap-4">
          <div className="min-w-0 space-y-1.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {product.title || 'Untitled Product'}
              </h1>
              {typeLabel && <Badge variant="outline">{typeLabel}</Badge>}
              {/* The detail lists live variants only, so a product whose
                  variants are ALL archived is told apart by its count:
                  Print Run's Archived wins over any Shopify status. */}
              <SkuStatusBadge
                status={
                  allVariantsArchived
                    ? 'archived'
                    : resolveProductStatus({
                        shopifyStatus: product.shopifyStatus,
                        productionType: product.productionType,
                        variants: product.variants,
                      })
                }
              />
            </div>

            <p className="text-sm text-muted-foreground">
              {product.productType && <span>{product.productType}</span>}
              {product.productType && <span className="mx-1.5 text-border">·</span>}
              <span>
                {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}
              </span>
            </p>

            {(shopifyUrl || product.specPdfUrl) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-0.5">
                {shopifyUrl && (
                  <a
                    href={shopifyUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    View in Shopify
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {product.specPdfUrl && (
                  <a
                    href={product.specPdfUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Spec PDF
                    <Download className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {canManageSkus && (
            <div className="flex shrink-0 items-center gap-2">
              <Button
                variant="outline"
                onClick={openEditProduct}
                className="h-8 px-3 text-xs gap-1.5"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
              {isPod && (
                <Button
                  variant="destructive"
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="h-8 px-3 text-xs gap-1.5"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Shopify metadata (PAP products) */}
      {!isPod && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Shopify Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              {product.handle && (
                <div>
                  <span className="text-muted-foreground">Handle</span>
                  <p className="font-mono text-xs mt-0.5">{product.handle}</p>
                </div>
              )}
              {product.shopifyProductGid && (
                <div>
                  <span className="text-muted-foreground">Product GID</span>
                  <p className="font-mono text-xs mt-0.5 truncate">{product.shopifyProductGid}</p>
                </div>
              )}
              {product.papMetafieldValue && (
                <div>
                  <span className="text-muted-foreground">PAP Metafield</span>
                  <p className="font-mono text-xs mt-0.5">{product.papMetafieldValue}</p>
                </div>
              )}
              {/* The fresh Shopify status; hidden when the product has none
                  (no Shopify id), never the column default. */}
              {product.shopifyStatus && (
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <p className="mt-0.5 capitalize">{product.shopifyStatus}</p>
                </div>
              )}
            </div>
            {product.tags.length > 0 && (
              <div className="mt-4">
                <span className="text-sm text-muted-foreground">Tags</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {product.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary" className="text-xs gap-1">
                      <Tag className="h-3 w-3" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Selection toolbar */}
      {canManageSkus && selectedCount > 0 && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/50 px-4 py-2">
          <span className="text-sm font-medium">
            {selectedCount} variant{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <Button variant="default" size="sm" onClick={() => setBatchEditOpen(true)}>
            Batch Edit
          </Button>
          {/* Archive is a reversible removal: orange outline (DESIGN §3.4, §13.6). */}
          <Button
            variant="outline"
            size="sm"
            className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
            onClick={openArchive}
          >
            <Archive className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('products.bulkBar.archive', { count: selectedCount })}
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      )}

      {canManageSkus && (
        <ArchiveSkusDialog
          open={archiveTargets !== null}
          onOpenChange={(next) => {
            if (!next) setArchiveTargets(null);
          }}
          mode="archive"
          targets={archiveTargets ?? []}
          onDone={finishArchive}
        />
      )}

      {/* Variants */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">
              Variants ({product.variants.length})
            </CardTitle>
            {isPod && canManageSkus && (
              <Button variant="outline" size="sm" onClick={() => setAddVariantOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Variant
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {product.variants.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              {allVariantsArchived
                ? t('productDetail.allVariantsArchived')
                : 'No variants linked to this product.'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canManageSkus && (
                    <TableHead className="w-10 px-3">
                      <Checkbox
                        checked={isAllSelected ? true : isSomeSelected ? 'indeterminate' : false}
                        onCheckedChange={selectAll}
                        aria-label="Select all variants"
                      />
                    </TableHead>
                  )}
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead>HS Code</TableHead>
                  <TableHead>Weight</TableHead>
                  {canSeeCosts && <TableHead className="text-right w-28">Cost</TableHead>}
                  {canSeeCosts && <TableHead className="text-right w-28">Default cost</TableHead>}
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedVariants.map((variant) => {
                  const isExpanded = expandedVariantIds.has(variant.id);
                  const locations = variant.locations ?? [];
                  // 8 columns always (image, SKU, type, size, color, HS code, weight, actions)
                  // + 1 checkbox for SKU managers, 1 cost and 1 default cost column for admin
                  const colCount = 8 + (canManageSkus ? 1 : 0) + (canSeeCosts ? 2 : 0);
                  return (
                    <Fragment key={variant.id}>
                    <TableRow
                      data-state={selectedIds.has(variant.id) ? 'selected' : undefined}
                    >
                      {canManageSkus && (
                        <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(variant.id)}
                            onCheckedChange={() => toggleSelect(variant.id)}
                            onClick={(e) => {
                              if (e.shiftKey) {
                                e.preventDefault();
                                toggleSelect(variant.id, e);
                              }
                            }}
                            aria-label={`Select ${variant.sku}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="p-2">
                        <ProductImageWithHover
                          imageUrl={variant.imageUrl ?? product.imageUrl ?? undefined}
                          productSku={variant.sku}
                          size="large"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <span className="inline-flex items-center gap-1.5">
                          {variant.sku}
                          {/* BLU-673: per-SKU print technology — POD only, shown only when set */}
                          {variant.productionType === 'POD' && variant.printTechnology && (
                            <Badge variant="outline" size="sm" className="font-sans">
                              {variant.printTechnology}
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {variant.category ?? product.productType ?? '-'}
                      </TableCell>
                      <TableCell>{variant.size || '-'}</TableCell>
                      <TableCell>{variant.color || '-'}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {variant.hsCode || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatWeight(variant.weightValue, variant.weightUnit)}
                      </TableCell>
                      {canSeeCosts && (
                      <TableCell className="text-right">
                        <VariantCostCell
                          sku={variant.sku}
                          locations={locations}
                          skuCostOverride={variant.skuCostOverrideOrg}
                          isExpanded={isExpanded}
                          onToggleExpand={() => toggleExpand(variant.id)}
                        />
                      </TableCell>
                      )}
                      {canSeeCosts && (
                        <TableCell className="text-right">
                          {variant.skuCostOverride != null ? (
                            <span className="font-medium tabular-nums">
                              {formatConverted(variant.skuCostOverride, variant.skuCostOverrideCurrency, variant.skuCostOverrideOrg)}
                            </span>
                          ) : (
                            <span className="text-sm text-muted-foreground">Not set</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditVariant(variant)}
                            title="Edit variant"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isPod && canManageSkus && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                removeVariantMutation.mutate({
                                  productId: product.id,
                                  skuId: variant.id,
                                })
                              }
                              disabled={removeVariantMutation.isPending}
                              title="Remove variant from product"
                            >
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {canSeeCosts && isExpanded && locations.length > 0 && (
                      <TableRow className="bg-muted/20 hover:bg-muted/20 border-l-2 border-l-primary/50" role="region" aria-label={`Location details for ${variant.sku}`}>
                        <TableCell colSpan={colCount} className="p-0">
                          <div className="px-6 py-3">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-border/50">
                                    <th className="text-left font-medium text-muted-foreground px-3 py-2 uppercase text-xs tracking-wide">Location</th>
                                    <th className="text-right font-medium text-muted-foreground px-3 py-2 uppercase text-xs tracking-wide w-20">On Hand</th>
                                    <th className="text-right font-medium text-muted-foreground px-3 py-2 uppercase text-xs tracking-wide w-24">Committed</th>
                                    <th className="text-right font-medium text-muted-foreground px-3 py-2 uppercase text-xs tracking-wide w-24">Available</th>
                                    <th className="text-right font-medium text-muted-foreground px-3 py-2 uppercase text-xs tracking-wide w-28">Cost</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-border/30">
                                  {locations.map((loc) => {
                                    // BLU-1268: a just-in-time pair has no count (its figures are
                                    // the store constant), so it shows none; the badge by the name
                                    // says why. BLU-1526: the same when its supplier is out of it,
                                    // with the chip beside the badge.
                                    const justInTime = loc.stockMode === 'just_in_time';
                                    const notCounted = (
                                      <span className="text-muted-foreground">
                                        <span aria-hidden="true">-</span>
                                        <span className="sr-only">{t('supply.notCounted')}</span>
                                      </span>
                                    );
                                    return (
                                    <tr key={loc.locationId} className="hover:bg-muted/40 transition-colors">
                                      <td className="px-3 py-2 font-medium max-w-sm">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          <span className="min-w-0 truncate">{loc.locationName}</span>
                                          {justInTime && (
                                            <SupplyBadge mode="just_in_time" srPrefix={t('baseGarments.supply.label')} />
                                          )}
                                          {loc.supplierOut === true && (
                                            <SupplierOutChip discontinued={loc.supplierDiscontinued === true} />
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-right tabular-nums">{justInTime ? notCounted : loc.currentStock}</td>
                                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{justInTime ? notCounted : loc.committedQuantity}</td>
                                      <td className={cn(
                                        'px-3 py-2 text-right tabular-nums font-medium',
                                        !justInTime && loc.availableQuantity < 0 && 'text-destructive'
                                      )}>{justInTime ? notCounted : loc.availableQuantity}</td>
                                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                        {loc.weightedAverageCost != null
                                          ? formatCost(loc.weightedAverageCost)
                                          : <span className="text-xs italic" title="Receive a purchase order to calculate cost">No receipts</span>}
                                      </td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* BLU-534: Bill of Materials — trims consumed as orders fulfil */}
      <BomCard productId={product.id} />

      {/* Add Variant Dialog */}
      <Dialog open={addVariantOpen} onOpenChange={setAddVariantOpen}>
        <DialogContent className="sm:max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!newVariantSku.trim()) return;
              addVariantMutation.mutate({
                productId: product.id,
                sku: newVariantSku.trim(),
                size: newVariantSize.trim() || undefined,
                color: newVariantColor.trim() || undefined,
              });
            }}
          >
            <DialogHeader>
              <DialogTitle>Add Variant</DialogTitle>
              <DialogDescription>
                Add a new SKU variant to {product.title || 'this product'}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <div className="space-y-2">
                <Label htmlFor="variant-sku">SKU Code *</Label>
                <Input
                  id="variant-sku"
                  value={newVariantSku}
                  onChange={(e) => setNewVariantSku(e.target.value)}
                  placeholder="e.g. CLASSIC-TEE-BLU-M"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="variant-size">Size</Label>
                  <Input
                    id="variant-size"
                    value={newVariantSize}
                    onChange={(e) => setNewVariantSize(e.target.value)}
                    placeholder="e.g. M"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="variant-color">Color</Label>
                  <Input
                    id="variant-color"
                    value={newVariantColor}
                    onChange={(e) => setNewVariantColor(e.target.value)}
                    placeholder="e.g. Blue"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddVariantOpen(false)}
                disabled={addVariantMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!newVariantSku.trim() || addVariantMutation.isPending}>
                {addVariantMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Variant'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editProductOpen} onOpenChange={setEditProductOpen}>
        <DialogContent className="sm:max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (isPod && !editTitle.trim()) return;
              const mutation: Record<string, unknown> = { productId: product.id };
              if (isPod) {
                mutation.title = editTitle.trim();
                mutation.productType = editProductType.trim() || undefined;
                mutation.imageUrl = editImageUrl.trim() || null;
              }
              mutation.specPdfUrl = editSpecPdfUrl.trim() || null;
              updateProductMutation.mutate(mutation as Parameters<typeof updateProductMutation.mutate>[0]);
            }}
          >
            <DialogHeader>
              <DialogTitle>Edit Product</DialogTitle>
              <DialogDescription>
                {isPod ? 'Update the product details.' : 'Manage the spec PDF for this product.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {isPod && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="edit-product-title">Title *</Label>
                    <Input
                      id="edit-product-title"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="e.g. Baby Tee"
                      required
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-product-type">Product Type</Label>
                    <Input
                      id="edit-product-type"
                      value={editProductType}
                      onChange={(e) => setEditProductType(e.target.value)}
                      placeholder="e.g. Baby Tee"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Product Image</Label>
                    {editImageUrl ? (
                      <div className="relative w-20 h-20">
                        <img
                          src={editImageUrl}
                          alt="Product"
                          className="w-20 h-20 rounded-md object-cover border"
                        />
                        <button
                          type="button"
                          className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground h-5 w-5 flex items-center justify-center"
                          onClick={() => setEditImageUrl('')}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
                        {imageUploading ? (
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        ) : (
                          <>
                            <ImagePlus className="h-6 w-6 text-muted-foreground mb-1" />
                            <span className="text-xs text-muted-foreground">Click to upload</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={imageUploading}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              setImageUploading(true);
                              const url = await inventoryApi.uploadProductImage(file, product.title || 'product');
                              setEditImageUrl(url);
                            } catch {
                              toast.error('Failed to upload image');
                            } finally {
                              setImageUploading(false);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                </>
              )}
              <div className="space-y-2">
                <Label>Spec / QC PDF</Label>
                {editSpecPdfUrl ? (
                  <div className="flex items-center gap-2">
                    <a
                      href={editSpecPdfUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline truncate max-w-[250px]"
                    >
                      <FileText className="h-4 w-4 shrink-0" />
                      Spec PDF
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => setEditSpecPdfUrl('')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
                    {pdfUploading ? (
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-muted-foreground mb-1" />
                        <span className="text-xs text-muted-foreground">Upload PDF</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      disabled={pdfUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          setPdfUploading(true);
                          const url = await inventoryApi.uploadSpecPdf(file, product.title || 'product');
                          setEditSpecPdfUrl(url);
                        } catch {
                          toast.error('Failed to upload PDF');
                        } finally {
                          setPdfUploading(false);
                        }
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditProductOpen(false)}
                disabled={updateProductMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={(isPod && !editTitle.trim()) || updateProductMutation.isPending || imageUploading || pdfUploading}>
                {updateProductMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Product Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <span className="font-semibold">{product.title || 'this product'}</span> and
              all {product.variants.length} variant{product.variants.length !== 1 ? 's' : ''}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteProductMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteProductMutation.isPending}
              onClick={() => deleteProductMutation.mutate({ productId: product.id })}
            >
              {deleteProductMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Product'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit SKU Modal */}
      <EditSkuModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        sku={editingSku}
        onUpdated={invalidate}
        globalMode
      />

      {/* Batch Edit Variants Modal */}
      <BatchEditVariantsModal
        isOpen={batchEditOpen}
        onClose={() => {
          setBatchEditOpen(false);
          clearSelection();
        }}
        variants={selectedVariantsForBatch}
        onUpdated={invalidate}
      />

      <BackToTopPill />
    </div>
  );
}
